import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm, type Control } from 'react-hook-form';
import { toast } from 'sonner';
import { customerInput } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { MaskedInput } from '@/components/shared/masked-input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { fmtDateTime, maskCpf, maskPhone } from '@/lib/format';
import { useBarbers, useTimezone } from '@/lib/org';
import type { CustomerDetail, CustomerRow } from './api';
import { applyServerError, emptyToNull, zodForm } from './lib';

interface Values {
  name: string;
  phone: string;
  email: string;
  cpf: string;
  birthDate: string;
  preferredBarberId: string;
  tags: string;
  preferences: string;
  notes: string;
  marketingConsent: boolean;
  whatsappOptOut: boolean;
}

const NONE = 'none';
const empty: Values = { name: '', phone: '', email: '', cpf: '', birthDate: '', preferredBarberId: NONE, tags: '', preferences: '', notes: '', marketingConsent: false, whatsappOptOut: false };

const toPayload = (v: Values) => ({
  name: v.name,
  phone: emptyToNull(v.phone),
  email: emptyToNull(v.email),
  cpf: emptyToNull(v.cpf),
  birthDate: emptyToNull(v.birthDate),
  preferredBarberId: v.preferredBarberId === NONE ? null : v.preferredBarberId,
  tags: v.tags.split(',').map((t) => t.trim()).filter(Boolean),
  preferences: v.preferences,
  notes: v.notes,
  marketingConsent: v.marketingConsent,
  whatsappOptOut: v.whatsappOptOut,
});

/** Caixa "Não enviar mensagens por WhatsApp" do cadastro de cliente. */
export function WhatsappOptOutField({ control }: { control: Control<{ whatsappOptOut: boolean }> }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border bg-muted/40 p-3">
      <Controller
        control={control}
        name="whatsappOptOut"
        render={({ field }) => (
          <Checkbox id="cus-wa-optout" checked={field.value} onCheckedChange={(v) => field.onChange(v === true)} aria-describedby="cus-wa-optout-text" className="mt-0.5" />
        )}
      />
      <div className="grid gap-1">
        <label htmlFor="cus-wa-optout" className="text-sm font-medium">
          Não enviar mensagens por WhatsApp
        </label>
        <p id="cus-wa-optout-text" className="text-xs text-muted-foreground">
          Marque se o cliente pediu para não receber confirmação, lembrete ou cancelamento de agendamento pelo WhatsApp.
        </p>
      </div>
    </div>
  );
}

export function CustomerFormModal({
  open,
  onOpenChange,
  customer,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  customer: (CustomerRow & Partial<CustomerDetail>) | null;
}) {
  const qc = useQueryClient();
  const tz = useTimezone();
  const barbers = useBarbers();
  const form = useForm<Values>({ resolver: zodForm<Values>(customerInput, toPayload), defaultValues: empty });
  const { errors } = form.formState;

  useEffect(() => {
    if (!open) return;
    form.reset(
      customer
        ? {
            name: customer.name,
            phone: customer.phone ? maskPhone(customer.phone) : '',
            email: customer.email ?? '',
            cpf: customer.cpf ? maskCpf(customer.cpf) : '',
            birthDate: customer.birthDate ?? '',
            preferredBarberId: customer.preferredBarberId ?? NONE,
            tags: customer.tags.join(', '),
            preferences: customer.preferences ?? '',
            notes: customer.notes ?? '',
            marketingConsent: customer.marketingConsent,
            whatsappOptOut: customer.whatsappOptOut ?? false,
          }
        : empty,
    );
  }, [open, customer]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: Values) => {
      const body = customerInput.parse(toPayload(v));
      return customer ? api.put(`/customers/${customer.id}`, body) : api.post('/customers', body);
    },
    onSuccess: () => {
      toast.success('Cliente salvo');
      qc.invalidateQueries({ queryKey: ['customers'] });
      onOpenChange(false);
    },
    onError: (e) => applyServerError(form, e),
  });

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={customer ? 'Editar cliente' : 'Novo cliente'}
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button type="submit" form="customer-form" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar cliente'}
          </Button>
        </>
      }
    >
      <form id="customer-form" onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid gap-4" noValidate>
        <Field id="cus-name" label="Nome" error={errors.name?.message}>
          <Input id="cus-name" autoFocus aria-invalid={!!errors.name} {...form.register('name')} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="cus-phone" label="Telefone" error={errors.phone?.message}>
            <MaskedInput mask="phone" id="cus-phone" placeholder="(11) 99999-0000" aria-invalid={!!errors.phone} {...form.register('phone')} />
          </Field>
          <Field id="cus-email" label="E-mail" error={errors.email?.message}>
            <Input id="cus-email" type="email" autoComplete="off" aria-invalid={!!errors.email} {...form.register('email')} />
          </Field>
          <Field id="cus-cpf" label="CPF (opcional)" error={errors.cpf?.message}>
            <MaskedInput mask="cpf" id="cus-cpf" placeholder="000.000.000-00" aria-invalid={!!errors.cpf} {...form.register('cpf')} />
          </Field>
          <Field id="cus-birth" label="Nascimento (opcional)" error={errors.birthDate?.message}>
            <Input id="cus-birth" type="date" {...form.register('birthDate')} />
          </Field>
          <Field id="cus-barber" label="Barbeiro preferido">
            <Controller
              control={form.control}
              name="preferredBarberId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="cus-barber" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Sem preferência</SelectItem>
                    {barbers.data?.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field id="cus-tags" label="Etiquetas" hint="Separe por vírgula. Ex.: vip, barba" error={errors.tags?.message}>
            <Input id="cus-tags" {...form.register('tags')} />
          </Field>
        </div>
        <Field id="cus-pref" label="Preferências" error={errors.preferences?.message}>
          <Textarea id="cus-pref" rows={2} maxLength={1000} placeholder="Ex.: degradê baixo, máquina 2" {...form.register('preferences')} />
        </Field>
        <Field id="cus-notes" label="Observações internas" error={errors.notes?.message}>
          <Textarea id="cus-notes" rows={2} maxLength={2000} {...form.register('notes')} />
        </Field>
        <div className="flex items-start gap-3 rounded-lg border bg-muted/40 p-3">
          <Controller
            control={form.control}
            name="marketingConsent"
            render={({ field }) => (
              <Switch id="cus-consent" checked={field.value} onCheckedChange={field.onChange} aria-describedby="cus-consent-text" className="mt-0.5" />
            )}
          />
          <div className="grid gap-1">
            <label htmlFor="cus-consent" className="text-sm font-medium">
              Aceita receber comunicações da barbearia
            </label>
            <p id="cus-consent-text" className="text-xs text-muted-foreground">
              Marque somente se o cliente concordou em receber promoções e novidades por e-mail (LGPD). Sem este aceite ele não entra em campanhas, e pode retirar o
              consentimento quando quiser.
              {customer?.marketingConsentAt && ` Aceite registrado em ${fmtDateTime(customer.marketingConsentAt, tz)}.`}
            </p>
          </div>
        </div>
        <WhatsappOptOutField control={form.control as unknown as Control<{ whatsappOptOut: boolean }>} />
      </form>
    </FormModal>
  );
}
